import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Sparkles, Receipt, User as UserIcon, Calendar, Check, X } from 'lucide-react'
import { Button, Card, Tabs, cn } from '../design'
import { useAuth } from '../auth/AuthContext'
import { getAgentStatus, setAgentKey, clearAgentKey } from '../api/agent'
import { getDkBooksStatus, setDkBooksToken, clearDkBooksToken } from '../api/dkbooks'
import { getGCalStatus, getGCalConnectUrl, disconnectGCal } from '../api/gcal'

/**
 * P12c — Settings page. Replaces P3 placeholder. Tabs:
 *   Profile (read-only for now)
 *   dkbooks (token + base URL)
 *   Agent (Anthropic API key + model)
 *   Calendar (Google OAuth — placeholder, P12c-3)
 */
export default function Settings() {
  const [params, setParams] = useSearchParams()
  const calStatus = params.get('calendar')
  const calMsg = params.get('msg')
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (calStatus === 'connected') {
      setBanner({ kind: 'ok', text: 'Google Calendar connected.' })
      const next = new URLSearchParams(params); next.delete('calendar'); next.delete('msg'); setParams(next, { replace: true })
    } else if (calStatus === 'error') {
      setBanner({ kind: 'err', text: calMsg ?? 'Google Calendar connection failed.' })
      const next = new URLSearchParams(params); next.delete('calendar'); next.delete('msg'); setParams(next, { replace: true })
    }
  }, [calStatus, calMsg, params, setParams])

  const defaultTab = calStatus ? 'calendar' : 'profile'

  return (
    <div className="mx-auto max-w-4xl px-6 py-6">
      <h1 className="text-xl font-semibold tracking-tight mb-1">Settings</h1>
      <p className="text-sm text-fg-subtle mb-5">
        Configure integrations and personal preferences. Per-user; nothing is shared with the workspace.
      </p>

      {banner && (
        <div className={cn(
          'mb-4 rounded-md border px-3 py-2 text-sm',
          banner.kind === 'ok' ? 'border-success/40 bg-success/10 text-success' : 'border-danger/40 bg-danger/10 text-danger',
        )}>{banner.text}</div>
      )}

      <Tabs
        defaultValue={defaultTab}
        items={[
          { value: 'profile',  label: <Tab icon={<UserIcon size={13} />}>Profile</Tab>, content: <ProfilePane /> },
          { value: 'dkbooks',  label: <Tab icon={<Receipt size={13} />}>dkbooks</Tab>,  content: <DkBooksPane /> },
          { value: 'agent',    label: <Tab icon={<Sparkles size={13} />}>Planning Agent</Tab>, content: <AgentPane /> },
          { value: 'calendar', label: <Tab icon={<Calendar size={13} />}>Calendar</Tab>, content: <CalendarPane /> },
        ]}
      />
    </div>
  )
}

function Tab({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5">{icon}{children}</span>
}

// ─── Profile ─────────────────────────────────────────────────────────────

function ProfilePane() {
  const { user } = useAuth()
  if (!user) return null
  return (
    <Card className="mt-4">
      <Row label="Name"           value={user.name} />
      <Row label="Email"          value={user.email} />
      <Row label="Role"           value={user.role} />
      <Row label="Timezone"       value={user.timezone} />
      <Row label="Date format"    value={user.date_format} />
      <p className="text-[10px] text-fg-subtle mt-3 italic">
        Profile editing endpoint not yet exposed (planned P12c+). Contact admin for changes.
      </p>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-b border-border last:border-b-0">
      <span className="text-[10px] uppercase tracking-wider text-fg-subtle font-medium pt-0.5">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  )
}

// ─── Agent ───────────────────────────────────────────────────────────────

function AgentPane() {
  const qc = useQueryClient()
  const sQ = useQuery({ queryKey: ['agent', 'status'], queryFn: getAgentStatus, staleTime: 10_000 })
  const [apiKey, setApiKey] = useState('')
  const [model,  setModel]  = useState('claude-sonnet-4-6')
  const [error,  setError]  = useState<string | null>(null)

  const setMut = useMutation({
    mutationFn: () => setAgentKey(apiKey, model),
    onSuccess: () => { setApiKey(''); setError(null); qc.invalidateQueries({ queryKey: ['agent'] }) },
    onError: (e: Error) => setError(e.message),
  })
  const clearMut = useMutation({
    mutationFn: clearAgentKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent'] }),
  })

  return (
    <Card className="mt-4">
      <h3 className="text-sm font-semibold mb-1">Anthropic API key</h3>
      <p className="text-xs text-fg-subtle mb-4">
        Required for the Planning Agent. Get one at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="text-accent hover:underline">console.anthropic.com</a>.
        Stored per-user; never sent to the workspace.
      </p>

      <ConnectionBadge connected={sQ.data?.connected} prefix={sQ.data?.key_prefix} />

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2 mt-4">
        <input
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder={sQ.data?.connected ? 'Enter new key to replace' : 'sk-ant-…'}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />
        <select
          value={model}
          onChange={e => setModel(e.target.value)}
          className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm"
        >
          <option value="claude-sonnet-4-6">Sonnet 4.6 (recommended)</option>
          <option value="claude-haiku-4-5">Haiku 4.5 (fast/cheap)</option>
          <option value="claude-opus-4-7">Opus 4.7 (smartest)</option>
        </select>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
      )}

      <div className="flex gap-2 mt-3">
        <Button onClick={() => setMut.mutate()} disabled={!apiKey || setMut.isPending}>
          {setMut.isPending ? 'Saving…' : (sQ.data?.connected ? 'Replace key' : 'Save key')}
        </Button>
        {sQ.data?.connected && (
          <Button variant="outline" onClick={() => clearMut.mutate()} disabled={clearMut.isPending}>
            Disconnect
          </Button>
        )}
      </div>
    </Card>
  )
}

// ─── dkbooks ─────────────────────────────────────────────────────────────

function DkBooksPane() {
  const qc = useQueryClient()
  const sQ = useQuery({ queryKey: ['dkbooks', 'status'], queryFn: getDkBooksStatus, staleTime: 10_000 })
  const [token, setToken] = useState('')
  const [baseUrl, setBaseUrl] = useState('https://dkbooks.diretek.com')
  const [error, setError] = useState<string | null>(null)

  const setMut = useMutation({
    mutationFn: () => setDkBooksToken(token, baseUrl),
    onSuccess: () => { setToken(''); setError(null); qc.invalidateQueries({ queryKey: ['dkbooks'] }) },
    onError: (e: Error) => setError(e.message),
  })
  const clearMut = useMutation({
    mutationFn: clearDkBooksToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dkbooks'] }),
  })

  return (
    <Card className="mt-4">
      <h3 className="text-sm font-semibold mb-1">dkbooks API token</h3>
      <p className="text-xs text-fg-subtle mb-4">
        Required for time tracking on stories. Mint a token in dkbooks → Settings → API tokens.
        Per-user; the token is validated on save.
      </p>

      <ConnectionBadge connected={sQ.data?.connected} prefix={sQ.data?.token_prefix} />

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-2 mt-4">
        <input
          type="password"
          value={token}
          onChange={e => setToken(e.target.value)}
          placeholder={sQ.data?.connected ? 'Enter new token to replace' : 'dkb_…'}
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />
        <input
          type="url"
          value={baseUrl}
          onChange={e => setBaseUrl(e.target.value)}
          className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm font-mono"
        />
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
      )}
      {sQ.data?.error && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg">
          last validation error: {sQ.data.error}
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <Button onClick={() => setMut.mutate()} disabled={!token || setMut.isPending}>
          {setMut.isPending ? 'Saving…' : (sQ.data?.connected ? 'Replace token' : 'Save token')}
        </Button>
        {sQ.data?.connected && (
          <Button variant="outline" onClick={() => clearMut.mutate()} disabled={clearMut.isPending}>
            Disconnect
          </Button>
        )}
      </div>
    </Card>
  )
}

function CalendarPane() {
  const qc = useQueryClient()
  const sQ = useQuery({ queryKey: ['gcal', 'status'], queryFn: getGCalStatus, staleTime: 10_000 })
  const [error, setError] = useState<string | null>(null)

  const connectMut = useMutation({
    mutationFn: getGCalConnectUrl,
    onSuccess: ({ authorize_url }) => {
      window.location.href = authorize_url
    },
    onError: (e: Error) => setError(e.message),
  })
  const disconnectMut = useMutation({
    mutationFn: disconnectGCal,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gcal'] }),
    onError: (e: Error) => setError(e.message),
  })

  const status = sQ.data
  const configured = status?.app_configured ?? false
  const connected = status?.connected ?? false

  return (
    <Card className="mt-4">
      <h3 className="text-sm font-semibold mb-1">Google Calendar</h3>
      <p className="text-xs text-fg-subtle mb-4">
        Read-only sync of upcoming meetings into the Atlas calendar view. Per-user OAuth — your tokens are stored encrypted on the server.
      </p>

      {!configured && (
        <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg">
          Server has no Google OAuth credentials configured. Admin must populate <span className="font-mono">src/config/google.php</span>.
        </div>
      )}

      <ConnectionBadge connected={connected} prefix={status?.account_email ?? null} />

      {connected && (
        <div className="mt-3 space-y-1 text-xs text-fg-muted">
          {status?.account_email && <Row label="Account" value={status.account_email} />}
          {status?.calendar_id && <Row label="Calendar" value={status.calendar_id} />}
          {status?.connected_at && <Row label="Connected" value={new Date(status.connected_at).toLocaleString()} />}
          {status?.last_sync_at && <Row label="Last sync" value={new Date(status.last_sync_at).toLocaleString()} />}
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
      )}

      <div className="flex gap-2 mt-3">
        {!connected ? (
          <Button
            onClick={() => { setError(null); connectMut.mutate() }}
            disabled={!configured || connectMut.isPending}
          >
            {connectMut.isPending ? 'Redirecting…' : 'Connect Google Calendar'}
          </Button>
        ) : (
          <Button variant="outline" onClick={() => disconnectMut.mutate()} disabled={disconnectMut.isPending}>
            {disconnectMut.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        )}
      </div>
    </Card>
  )
}

function ConnectionBadge({ connected, prefix }: { connected?: boolean; prefix?: string | null }) {
  if (connected === undefined) return null
  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
      connected ? 'bg-success/15 text-success' : 'bg-bg-muted text-fg-muted',
    )}>
      {connected ? <Check size={12} /> : <X size={12} />}
      {connected ? <>connected{prefix && <span className="font-mono ml-1">· {prefix}…</span>}</> : 'not connected'}
    </div>
  )
}
